import React from 'react';
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";

const DataRefreshStatusPage: React.FC = () => {
  const { globalRefreshStatus, triggerManualRefresh } = useData();
  const { user } = useAuth();

  const formatTime = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getTimeUntilNext = () => {
    const now = new Date();
    const next = globalRefreshStatus.nextRefresh;
    const diff = next.getTime() - now.getTime();

    if (diff <= 0) return 'Any moment';

    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${minutes}m ${seconds}s`;
  };

  return (
    <div id="dashboard">
      <div className="h-full flex flex-col p-5 w-full dashboard-shadow max-w-8xl w-full mt-5">
        <div className="content-toolbar">
          <button className="jet" onClick={() => window.location.href = '/dashboard'}>
            <i className="material-symbols-outlined">arrow_back_ios</i>&ensp;Back to Dashboard
          </button>
        </div>

        <h1 className="pr-4 py-5 text-3xl font-light">Data Refresh Status</h1>
        <hr className="my-3 pb-5 -mx-5 border-rs-jet-light" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Current Status Card */}
          <div className="bg-rs-off-black border border-rs-jet rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-rs-green flex items-center">
                <i className="material-symbols-outlined mr-3">sync</i>
                Current Status
              </h3>
              {globalRefreshStatus.isRefreshing && (
                <i className="material-symbols-outlined animate-spin text-rs-green text-2xl">cached</i>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">Status:</span>
                <span className={`font-medium ${globalRefreshStatus.isRefreshing ? 'text-rs-green' : 'text-rs-off-white'}`}>
                  {globalRefreshStatus.isRefreshing ? 'Refreshing...' : 'Active'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">Interval:</span>
                <span className="text-rs-off-white font-medium">{globalRefreshStatus.intervalMinutes} minutes</span>
              </div>

              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">User Level:</span>
                <span className="text-rs-off-white font-medium">
                  {user?.is_admin ? 'Admin' : 'Regular User'}
                </span>
              </div>
            </div>
          </div>

          {/* Timing Information Card */}
          <div className="bg-rs-off-black border border-rs-jet rounded-lg p-6">
            <h3 className="text-lg font-semibold text-rs-green flex items-center mb-4">
              <i className="material-symbols-outlined mr-3">schedule</i>
              Timing Information
            </h3>

            <div className="flex flex-col gap-3">
              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">Last Refresh:</span>
                <span className="text-rs-off-white font-medium">{formatTime(globalRefreshStatus.lastRefresh)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">Next Refresh:</span>
                <span className="text-rs-off-white font-medium">{getTimeUntilNext()}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-rs-off-white opacity-70">Next Refresh Time:</span>
                <span className="text-rs-off-white font-medium">{formatTime(globalRefreshStatus.nextRefresh)}</span>
              </div>
            </div>
          </div>

          {/* Manual Control Card */}
          <div className="bg-rs-off-black border border-rs-jet rounded-lg p-6">
            <h3 className="text-lg font-semibold text-rs-green flex items-center mb-4">
              <i className="material-symbols-outlined mr-3">touch_app</i>
              Manual Control
            </h3>

            <div className="flex flex-col gap-4">
              <p className="text-rs-off-white opacity-70 text-sm">
                Force an immediate refresh of all data sources. This will update the cache with the latest data.
              </p>

              <button
                onClick={triggerManualRefresh}
                disabled={globalRefreshStatus.isRefreshing}
                className="w-full px-4 py-3 bg-rs-green text-rs-black rounded-lg hover:bg-rs-light-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {globalRefreshStatus.isRefreshing ? (
                  <>
                    <i className="material-symbols-outlined animate-spin mr-2">cached</i>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="material-symbols-outlined mr-2">refresh</i>
                    Force Refresh Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="mt-8 bg-rs-off-black border border-rs-jet rounded-lg p-6">
          <h3 className="text-lg font-semibold text-rs-green flex items-center mb-4">
            <i className="material-symbols-outlined mr-3">info</i>
            System Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-rs-off-white font-medium mb-3">Configuration</h4>
              <ul className="flex flex-col gap-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-rs-off-white opacity-70">Refresh Interval:</span>
                  <span className="text-rs-off-white">Every 30 minutes</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-rs-off-white opacity-70">Business Hours Only:</span>
                  <span className="text-rs-off-white">Yes (6 AM - 6 PM CST)</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-rs-off-white opacity-70">Skip Weekends:</span>
                  <span className="text-rs-off-white">Yes</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-rs-off-white opacity-70">Timezone:</span>
                  <span className="text-rs-off-white">America/Chicago (CST/CDT)</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-rs-off-white font-medium mb-3">Data Sources</h4>
              <ul className="flex flex-col gap-1 text-sm text-rs-off-white opacity-70">
                <li>• Capro Data</li>
                <li>• Petchem Data</li>
                <li>• Chlorine Data</li>
                <li>• Driver Data</li>
                <li>• ChemOrbis Data</li>
                <li>• PetMaster Pricing Data</li>
                <li>• Contracts Data</li>
                <li>• Refinery Rates Data</li>
                <li>• News Data</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRefreshStatusPage;
